import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { pagesAPI, faqsAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const Contato = () => {
  const [pageContent, setPageContent] = useState({});
  const [faqs, setFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const { themeConfig } = useTheme();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Carregar conteúdo da página e FAQs do backend em paralelo
      const [pageData, faqsData] = await Promise.all([
        pagesAPI.getByName('contato').catch(() => null),
        faqsAPI.getAll().catch(() => [])
      ]);

      if (pageData && pageData.content) {
        setPageContent(pageData.content);
      }

      if (faqsData && faqsData.length > 0) {
        setFaqs(faqsData);
      } else {
        // FAQs padrão caso a API não retorne
        setFaqs([
          { id: '1', question: 'Como faço para reservar um pacote?', answer: 'Você pode reservar diretamente pelo nosso site, clicando em "Reservar" no pacote desejado, ou entrar em contato pelo WhatsApp para atendimento personalizado.', featured: true },
          { id: '2', question: 'Quais são as formas de pagamento?', answer: 'Aceitamos PIX, boleto bancário e cartão de crédito em até 12x. O pagamento pode ser parcelado conforme condições de cada pacote.', featured: true },
          { id: '3', question: 'Posso cancelar minha reserva?', answer: 'Sim, é possível cancelar seguindo nossa política de cancelamento. Quanto antes o cancelamento, maior a porcentagem de reembolso.', featured: true },
          { id: '4', question: 'O que está incluso nos pacotes?', answer: 'Cada pacote possui itens específicos inclusos, como hospedagem, transporte, passeios e seguro. Consulte a página do pacote para detalhes.', featured: true },
          { id: '5', question: 'Como funciona o embarque nas viagens rodoviárias?', answer: 'Oferecemos pontos de embarque em locais estratégicos do Rio de Janeiro. Você escolhe o mais conveniente durante a reserva.', featured: true },
          { id: '6', question: 'A empresa possui cadastro no Ministério do Turismo?', answer: 'Sim! A TÔ INDO ALI possui registro no Cadastur, garantindo qualidade e segurança.', featured: true }
        ]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Pegar valores do conteúdo editável ou usar padrão
  const pageTitle = pageContent.pageTitle || 'Entre em Contato';
  const pageSubtitle = pageContent.pageSubtitle || 'Estamos prontos para ajudar você a planejar sua próxima aventura. Entre em contato e descubra o destino perfeito!';
  const whatsappNumber = pageContent.whatsappNumber || '5521979652791';
  const whatsappDisplay = pageContent.whatsappDisplay || '(21) 97965-2791';
  const email = pageContent.email || 'contato@toindoali.com.br';
  const address = pageContent.address || 'Rio de Janeiro, RJ';
  const workingHours = pageContent.workingHours || 'Segunda a Sexta: 9h às 18h';
  const whatsappMessage = pageContent.whatsappMessage || 'Olá! Vim pelo site da TÔ INDO ALI e quero informações sobre os pacotes de viagem.';

  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  const contactInfo = [
    { icon: <Phone className="w-6 h-6" />, title: 'Telefone', content: whatsappDisplay, link: `tel:+${whatsappNumber}` },
    { icon: <Mail className="w-6 h-6" />, title: 'E-mail', content: email, link: `mailto:${email}` },
    { icon: <MapPin className="w-6 h-6" />, title: 'Localização', content: address, link: null },
    { icon: <Clock className="w-6 h-6" />, title: 'Horário de Atendimento', content: workingHours, link: null }
  ];

  // FAQs em destaque
  const featuredFaqs = faqs.filter(f => f.featured).slice(0, 6);
  
  // Todas as FAQs para busca
  const filteredFaqs = searchTerm 
    ? faqs.filter(f => 
        f.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        f.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 overflow-x-hidden" style={{ backgroundColor: themeConfig?.theme?.backgroundLight || '#f9fafb' }}>
      {/* Hero - Menor no mobile */}
      <div 
        className="text-white py-10 md:py-16"
        style={{ backgroundColor: themeConfig?.theme?.primary || '#003366' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">{pageTitle}</h1>
          <p className="text-sm sm:text-base md:text-lg opacity-80 max-w-3xl">{pageSubtitle}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Informações de Contato */}
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Nossas Informações</h2>
            
            <div className="space-y-6 mb-12">
              {contactInfo.map((info, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0">
                        <div className="text-gray-900">{info.icon}</div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="font-semibold text-gray-900 mb-1">{info.title}</h3>
                        {info.link ? (
                          <a href={info.link} className="text-gray-600 hover:text-[#FFD700] transition-colors">
                            {info.content}
                          </a>
                        ) : (
                          <p className="text-gray-600">{info.content}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Botão WhatsApp */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6 text-center">
                <MessageCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Fale Conosco pelo WhatsApp</h3>
                <p className="text-gray-600 mb-4 text-sm">Atendimento rápido e personalizado</p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-all"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Iniciar Conversa</span>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Perguntas Frequentes */}
          <div id="faq">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Perguntas Frequentes</h2>
            
            {/* Busca */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                placeholder="Buscar uma pergunta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Resultados da busca */}
            {searchTerm && (
              <div className="mb-6">
                <p className="text-sm text-gray-600 mb-3">
                  {filteredFaqs.length} resultado(s) para "{searchTerm}"
                </p>
                {filteredFaqs.length > 0 ? (
                  <div className="space-y-3">
                    {filteredFaqs.map((faq) => (
                      <Card key={faq.id} className="border-l-4 border-l-[#FFD700]">
                        <CardContent className="p-4">
                          <button
                            onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                            className="w-full text-left flex items-center justify-between"
                          >
                            <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                            {expandedFaq === faq.id ? (
                              <ChevronUp className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            )}
                          </button>
                          {expandedFaq === faq.id && (
                            <p className="text-gray-600 text-sm mt-3 pt-3 border-t">{faq.answer}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Nenhuma pergunta encontrada. Entre em contato conosco!</p>
                )}
              </div>
            )}

            {/* FAQs em Destaque */}
            {!searchTerm && (
              <div className="space-y-4">
                {featuredFaqs.map((faq, index) => (
                  <Card key={faq.id || index}>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-gray-900 mb-2">{faq.question}</h3>
                      <p className="text-gray-600 text-sm">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-20 text-center">
          <Card className="bg-gradient-to-r from-[#FFD700] to-[#FFA500]">
            <CardContent className="p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Não encontrou o que procurava?
              </h2>
              <p className="text-xl text-gray-800 mb-8">
                Nossa equipe está pronta para atender você e esclarecer todas as suas dúvidas!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center space-x-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>WhatsApp</span>
                </a>
                <a
                  href={`mailto:${email}`}
                  className="inline-flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <Mail className="w-5 h-5" />
                  <span>E-mail</span>
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Contato;
